import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ScienceIcon from "@mui/icons-material/Science";
import { get, post } from "src/utils/networkUtils";

/**
 * End-to-end scenario tests. Each run mutates real (clearly-marked) test data
 * through the production scoring pipeline, then asserts the outcome: a match
 * that starts, a goal that lands in the database and earns exactly the points
 * the active rules say, a captain whose points double at full time.
 */
export default function ScenarioTestsPanel({ canRun }) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningKey, setRunningKey] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [results, setResults] = useState({});
  const [resetResult, setResetResult] = useState(null);
  const [error, setError] = useState(null);

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/admin/match-simulator/scenario-tests");
      setScenarios(res?.data ?? []);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to load scenario tests"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const runScenario = async (key) => {
    setRunningKey(key);
    setError(null);
    setResetResult(null);
    try {
      const res = await post("/admin/match-simulator/scenario-tests/run", {
        scenario: key,
      });
      setResults((prev) => ({ ...prev, [key]: res?.data ?? null }));
      loadScenarios();
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          `Failed to run scenario ${key}`
      );
    } finally {
      setRunningKey(null);
    }
  };

  const resetData = async () => {
    setResetting(true);
    setError(null);
    try {
      const res = await post("/admin/match-simulator/scenario-tests/reset", {});
      setResetResult(res?.data ?? null);
      setResults({});
      loadScenarios();
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to reset scenario test data"
      );
    } finally {
      setResetting(false);
    }
  };

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Scenario tests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each test writes real data through the production pipeline
                    (a simulator-locked test match in the dedicated
                    &quot;Scenario Tests&quot; gameweek), runs scoring, and
                    asserts the outcome step by step. Reset removes every
                    artifact.
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    canRun
                      ? ""
                      : "Enable MATCH_SIMULATOR_APPLY_ENABLED to run scenario tests"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={
                        resetting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RestartAltIcon />
                        )
                      }
                      onClick={resetData}
                      disabled={!canRun || resetting || Boolean(runningKey)}
                    >
                      Reset test data
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
              {resetResult && (
                <Alert severity="success">
                  {resetResult.steps
                    ?.map((step) => step.detail)
                    .filter(Boolean)
                    .join(" · ")}
                </Alert>
              )}
              {loading && <CircularProgress size={24} />}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {scenarios.map((scenario) => {
        const result = results[scenario.key];
        return (
          <Grid item xs={12} md={4} key={scenario.key}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={2} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScienceIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {scenario.name}
                      </Typography>
                    </Stack>
                    {result && (
                      <Chip
                        size="small"
                        color={result.passed ? "success" : "error"}
                        label={result.passed ? "PASSED" : "FAILED"}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {scenario.description}
                  </Typography>

                  {scenario.planned && (
                    <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                      {scenario.planned.fixture && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Will create: {scenario.planned.fixture}
                        </Typography>
                      )}
                      {scenario.planned.scorer && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Scorer will be: {scenario.planned.scorer}
                        </Typography>
                      )}
                      {scenario.planned.note && (
                        <Typography variant="caption" color="text.secondary">
                          {scenario.planned.note}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  <Box>
                    <Tooltip
                      title={
                        canRun
                          ? ""
                          : "Enable MATCH_SIMULATOR_APPLY_ENABLED to run scenario tests"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          startIcon={
                            runningKey === scenario.key ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                          onClick={() => runScenario(scenario.key)}
                          disabled={
                            !canRun || resetting || Boolean(runningKey)
                          }
                        >
                          Run test
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {result && (
                    <>
                      <Divider />
                      <List dense disablePadding>
                        {result.steps?.map((step, index) => (
                          <ListItem
                            key={`${scenario.key}-${index}`}
                            disableGutters
                            alignItems="flex-start"
                          >
                            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                              {step.ok ? (
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                              ) : (
                                <CancelIcon color="error" fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={step.name}
                              secondary={step.detail}
                              primaryTypographyProps={{
                                variant: "body2",
                                fontWeight: 600,
                              }}
                              secondaryTypographyProps={{
                                variant: "caption",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
